import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { getUserFriendlyError } from "@/lib/error-handler";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Shield, Plus, UserCog, ShieldCheck, ShieldOff, Users } from "lucide-react";
import { PermissionsMatrix } from "@/components/admin/PermissionsMatrix";

type AdminRole = {
  id: string;
  name: string;
  label: string;
  is_system_role: boolean;
  is_active: boolean;
  created_at: string;
};

type AdminUserRole = {
  id: string;
  user_id: string;
  admin_role_id: string;
  is_active: boolean;
  created_at: string;
};

type AdminWithRole = AdminUserRole & {
  email: string | null;
  visible_name: string | null;
  role_name: string;
  role_label: string;
};

export default function AdminAdmins() {
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newRoleId, setNewRoleId] = useState("");
  const [editRoleDialog, setEditRoleDialog] = useState<{ userId: string; currentRoleId: string; name: string } | null>(null);
  const [selectedNewRole, setSelectedNewRole] = useState("");
  const [confirmAction, setConfirmAction] = useState<{ type: "deactivate" | "activate"; userId: string; name: string } | null>(null);

  // Fetch roles
  const { data: roles, isLoading: rolesLoading } = useQuery({
    queryKey: ["admin-roles"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("admin_roles")
        .select("*")
        .order("name");
      if (error) throw error;
      return data as AdminRole[];
    },
  });

  // Fetch admin users with their roles
  const { data: admins, isLoading: adminsLoading } = useQuery({
    queryKey: ["admin-user-roles"],
    queryFn: async () => {
      const { data: aur, error: aurError } = await supabase
        .from("admin_user_roles")
        .select("*")
        .order("created_at", { ascending: false });
      if (aurError) throw aurError;

      if (!aur || aur.length === 0) return [];

      // Fetch profiles for these users
      const userIds = aur.map((a: any) => a.user_id);
      const { data: profiles, error: profError } = await supabase
        .from("profiles")
        .select("id, email, visible_name")
        .in("id", userIds);
      if (profError) throw profError;

      const profileMap = new Map((profiles ?? []).map((p: any) => [p.id, p]));

      // Fetch roles for mapping
      const { data: allRoles, error: rolesErr } = await supabase
        .from("admin_roles")
        .select("id, name, label");
      if (rolesErr) throw rolesErr;

      const roleMap = new Map((allRoles ?? []).map((r: any) => [r.id, r]));

      return aur.map((a: any) => {
        const profile = profileMap.get(a.user_id);
        const role = roleMap.get(a.admin_role_id);
        return {
          ...a,
          email: profile?.email ?? null,
          visible_name: profile?.visible_name ?? null,
          role_name: role?.name ?? "unknown",
          role_label: role?.label ?? "Desconocido",
        } as AdminWithRole;
      });
    },
  });

  // Count admins per role
  const roleAdminCounts = (roleId: string) =>
    admins?.filter((a) => a.admin_role_id === roleId && a.is_active).length ?? 0;

  // Add admin mutation
  const addAdminMutation = useMutation({
    mutationFn: async ({ email, roleId }: { email: string; roleId: string }) => {
      // Find user by email in profiles
      const { data: profile, error: profErr } = await supabase
        .from("profiles")
        .select("id")
        .eq("email", email)
        .single();
      if (profErr || !profile) throw new Error("No se encontró un usuario con ese email.");

      // Check if already admin
      const existing = admins?.find((a) => a.user_id === profile.id);
      if (existing) throw new Error("Este usuario ya tiene un rol de administración.");

      // Insert admin_user_roles
      const { error } = await supabase
        .from("admin_user_roles")
        .insert({ user_id: profile.id, admin_role_id: roleId } as any);
      if (error) throw error;

      // Also add to user_roles for backward compatibility (as admin)
      const { error: urErr } = await supabase
        .from("user_roles")
        .insert({ user_id: profile.id, role: "admin" } as any);
      // Ignore if already exists
      if (urErr && !urErr.message.includes("duplicate")) throw urErr;

      // Audit log
      await supabase.from("admin_audit_log").insert({
        admin_id: user!.id,
        action: "create_admin",
        entity_type: "admin",
        entity_id: profile.id,
        details: { email, role_id: roleId },
      } as any);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-user-roles"] });
      toast({ title: "Administrador añadido" });
      setAddDialogOpen(false);
      setNewEmail("");
      setNewRoleId("");
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: getUserFriendlyError(err), variant: "destructive" });
    },
  });

  // Change role mutation
  const changeRoleMutation = useMutation({
    mutationFn: async ({ userId, newRoleId }: { userId: string; newRoleId: string }) => {
      // Check last super_admin protection
      const currentAdmin = admins?.find((a) => a.user_id === userId);
      if (currentAdmin?.role_name === "super_admin") {
        const { data } = await supabase.rpc("is_last_super_admin", { _user_id: userId });
        if (data) {
          const newRole = roles?.find((r) => r.id === newRoleId);
          if (newRole?.name !== "super_admin") {
            throw new Error("No puedes degradar al último Super Administrador.");
          }
        }
      }

      const { error } = await supabase
        .from("admin_user_roles")
        .update({ admin_role_id: newRoleId } as any)
        .eq("user_id", userId);
      if (error) throw error;

      await supabase.from("admin_audit_log").insert({
        admin_id: user!.id,
        action: "change_role",
        entity_type: "admin",
        entity_id: userId,
        details: { old_role_id: currentAdmin?.admin_role_id, new_role_id: newRoleId },
      } as any);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-user-roles"] });
      toast({ title: "Rol actualizado" });
      setEditRoleDialog(null);
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: getUserFriendlyError(err), variant: "destructive" });
    },
  });

  // Activate/Deactivate mutation
  const toggleActiveMutation = useMutation({
    mutationFn: async ({ userId, activate }: { userId: string; activate: boolean }) => {
      if (!activate) {
        const currentAdmin = admins?.find((a) => a.user_id === userId);
        if (currentAdmin?.role_name === "super_admin") {
          const { data } = await supabase.rpc("is_last_super_admin", { _user_id: userId });
          if (data) throw new Error("No puedes desactivar al último Super Administrador.");
        }
      }

      const { error } = await supabase
        .from("admin_user_roles")
        .update({ is_active: activate } as any)
        .eq("user_id", userId);
      if (error) throw error;

      await supabase.from("admin_audit_log").insert({
        admin_id: user!.id,
        action: activate ? "activate_admin" : "deactivate_admin",
        entity_type: "admin",
        entity_id: userId,
      } as any);
    },
    onSuccess: (_, { activate }) => {
      queryClient.invalidateQueries({ queryKey: ["admin-user-roles"] });
      toast({ title: activate ? "Acceso activado" : "Acceso desactivado" });
      setConfirmAction(null);
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: getUserFriendlyError(err), variant: "destructive" });
      setConfirmAction(null);
    },
  });

  const isLoading = rolesLoading || adminsLoading;

  return (
    <AdminLayout requiredModule="admins_roles" requiredPermission="can_view">
      <div>
        <div className="mb-6">
          <h1 className="font-display text-2xl font-bold flex items-center gap-2">
            <Shield className="h-6 w-6" /> Admins y Roles
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Gestiona administradores, roles y permisos del sistema
          </p>
        </div>

        <Tabs defaultValue="admins" className="space-y-6">
          <TabsList>
            <TabsTrigger value="admins" className="gap-2">
              <UserCog className="h-4 w-4" /> Administradores
            </TabsTrigger>
            <TabsTrigger value="roles" className="gap-2">
              <Shield className="h-4 w-4" /> Roles y Permisos
            </TabsTrigger>
          </TabsList>

          {/* -- TAB: Administradores -- */}
          <TabsContent value="admins" className="space-y-4">
            <div className="flex justify-end">
              <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
                <DialogTrigger asChild>
                  <Button size="sm">
                    <Plus className="mr-2 h-4 w-4" /> Añadir administrador
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Añadir administrador</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Email del usuario</Label>
                      <Input
                        type="email"
                        placeholder="usuario@ejemplo.com"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                      />
                      <p className="text-xs text-muted-foreground">
                        El usuario debe estar registrado previamente en la plataforma.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label>Rol</Label>
                      <Select value={newRoleId} onValueChange={setNewRoleId}>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar rol" />
                        </SelectTrigger>
                        <SelectContent>
                          {roles?.filter((r) => r.is_active).map((r) => (
                            <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" onClick={() => setAddDialogOpen(false)}>Cancelar</Button>
                      <Button
                        disabled={!newEmail || !newRoleId || addAdminMutation.isPending}
                        onClick={() => addAdminMutation.mutate({ email: newEmail, roleId: newRoleId })}
                      >
                        {addAdminMutation.isPending ? "Añadiendo…" : "Añadir"}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => <Skeleton key={i} className="h-12" />)}
              </div>
            ) : !admins?.length ? (
              <div className="py-16 text-center text-muted-foreground">
                <Users className="mx-auto mb-2 h-8 w-8 opacity-40" />
                <p>No hay administradores configurados</p>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nombre</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Rol</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Fecha alta</TableHead>
                      <TableHead>Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {admins.map((admin) => (
                      <TableRow key={admin.id} className={!admin.is_active ? "opacity-50" : ""}>
                        <TableCell className="font-medium">
                          {admin.visible_name || "—"}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {admin.email ?? "—"}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{admin.role_label}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={admin.is_active ? "bg-success/10 text-success" : "bg-destructive/10 text-destructive"}>
                            {admin.is_active ? "Activo" : "Inactivo"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {new Date(admin.created_at).toLocaleDateString("es-ES")}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              title="Cambiar rol"
                              onClick={() => {
                                setEditRoleDialog({
                                  userId: admin.user_id,
                                  currentRoleId: admin.admin_role_id,
                                  name: admin.visible_name || admin.email || "Admin",
                                });
                                setSelectedNewRole(admin.admin_role_id);
                              }}
                            >
                              <UserCog className="h-4 w-4" />
                            </Button>
                            {admin.is_active ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                title="Desactivar acceso"
                                onClick={() => setConfirmAction({
                                  type: "deactivate",
                                  userId: admin.user_id,
                                  name: admin.visible_name || admin.email || "Admin",
                                })}
                              >
                                <ShieldOff className="h-4 w-4 text-destructive" />
                              </Button>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                title="Activar acceso"
                                onClick={() => setConfirmAction({
                                  type: "activate",
                                  userId: admin.user_id,
                                  name: admin.visible_name || admin.email || "Admin",
                                })}
                              >
                                <ShieldCheck className="h-4 w-4 text-success" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>

          {/* -- TAB: Roles y Permisos -- */}
          <TabsContent value="roles" className="space-y-6">
            {rolesLoading ? (
              <Skeleton className="h-48" />
            ) : (
              <>
                {/* Roles summary */}
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  {roles?.map((role) => (
                    <div key={role.id} className="rounded-lg border p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-display text-sm font-semibold">{role.label}</h3>
                        <Badge variant={role.is_active ? "default" : "secondary"}>
                          {role.is_active ? "Activo" : "Inactivo"}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mb-1">
                        {roleAdminCounts(role.id)} admin{roleAdminCounts(role.id) !== 1 ? "s" : ""} asignado{roleAdminCounts(role.id) !== 1 ? "s" : ""}
                      </p>
                      {role.is_system_role && (
                        <Badge variant="outline" className="text-xs">Rol del sistema</Badge>
                      )}
                    </div>
                  ))}
                </div>

                {/* Permissions matrix for each role */}
                {roles?.filter((r) => r.is_active).map((role) => (
                  <PermissionsMatrix key={role.id} roleId={role.id} roleLabel={role.label} roleName={role.name} />
                ))}
              </>
            )}
          </TabsContent>
        </Tabs>

        {/* Change Role Dialog */}
        <Dialog open={!!editRoleDialog} onOpenChange={(o) => { if (!o) setEditRoleDialog(null); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Cambiar rol de {editRoleDialog?.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Nuevo rol</Label>
                <Select value={selectedNewRole} onValueChange={setSelectedNewRole}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {roles?.filter((r) => r.is_active).map((r) => (
                      <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditRoleDialog(null)}>Cancelar</Button>
                <Button
                  disabled={changeRoleMutation.isPending || selectedNewRole === editRoleDialog?.currentRoleId}
                  onClick={() => {
                    if (editRoleDialog) {
                      changeRoleMutation.mutate({ userId: editRoleDialog.userId, newRoleId: selectedNewRole });
                    }
                  }}
                >
                  {changeRoleMutation.isPending ? "Guardando…" : "Guardar"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Confirm Activate/Deactivate Dialog */}
        <AlertDialog open={!!confirmAction} onOpenChange={(o) => { if (!o) setConfirmAction(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {confirmAction?.type === "deactivate" ? "Desactivar acceso" : "Activar acceso"}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {confirmAction?.type === "deactivate"
                  ? `¿Seguro que quieres desactivar el acceso de administración de ${confirmAction?.name}? No podrá acceder al panel.`
                  : `¿Seguro que quieres reactivar el acceso de administración de ${confirmAction?.name}?`
                }
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  if (confirmAction) {
                    toggleActiveMutation.mutate({
                      userId: confirmAction.userId,
                      activate: confirmAction.type === "activate",
                    });
                  }
                }}
              >
                {confirmAction?.type === "deactivate" ? "Desactivar" : "Activar"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
}
